import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '@/services/api';
import {
  FileText,
  Bug,
  MessageSquare,
  PlusCircle,
  Search,
  Filter,
  ChevronUp,
  ChevronDown,
  ThumbsUp,
  MessageCircle,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { ItemType, ItemStatus, ItemPriority, ItemFilters, ItemSort } from '@/types';

const typeConfig: Record<ItemType, { icon: typeof FileText; label: string; color: string; bgColor: string }> = {
  requirement: { icon: FileText, label: 'Feature Request', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/50' },
  feedback: { icon: MessageSquare, label: 'Feedback', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-100 dark:bg-purple-900/50' },
  bug: { icon: Bug, label: 'Bug', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/50' },
};

const statusConfig: Record<ItemStatus, { label: string; color: string; bgColor: string }> = {
  new: { label: 'New', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/50' },
  triaged: { label: 'Triaged', color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-100 dark:bg-indigo-900/50' },
  planned: { label: 'Planned', color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-100 dark:bg-cyan-900/50' },
  in_progress: { label: 'In Progress', color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-100 dark:bg-yellow-900/50' },
  review: { label: 'In Review', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-100 dark:bg-orange-900/50' },
  completed: { label: 'Completed', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/50' },
  rejected: { label: 'Rejected', color: 'text-gray-600 dark:text-gray-400', bgColor: 'bg-gray-100 dark:bg-gray-700' },
  duplicate: { label: 'Duplicate', color: 'text-gray-600 dark:text-gray-400', bgColor: 'bg-gray-100 dark:bg-gray-700' },
};

const priorityConfig: Record<ItemPriority, { label: string; color: string }> = {
  critical: { label: 'Critical', color: 'text-red-600 dark:text-red-400' },
  high: { label: 'High', color: 'text-orange-600 dark:text-orange-400' },
  medium: { label: 'Medium', color: 'text-yellow-600 dark:text-yellow-400' },
  low: { label: 'Low', color: 'text-gray-600 dark:text-gray-400' },
};

export function Items() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);

  // Parse filters from URL
  const filters: ItemFilters = {
    type: searchParams.get('type') as ItemType | undefined,
    status: searchParams.getAll('status') as ItemStatus[],
    priority: searchParams.getAll('priority') as ItemPriority[],
    search: searchParams.get('search') || undefined,
  };

  const sort: ItemSort = {
    field: (searchParams.get('sort_by') as any) || 'created_at',
    direction: (searchParams.get('sort_direction') as any) || 'desc',
  };

  const page = parseInt(searchParams.get('page') || '1', 10);

  const { data, isLoading } = useQuery({
    queryKey: ['items', filters, sort, page],
    queryFn: () => apiClient.getItems(filters, sort, page, 20),
  });

  const updateFilters = (newFilters: Partial<ItemFilters>) => {
    const params = new URLSearchParams(searchParams);

    if (newFilters.type !== undefined) {
      if (newFilters.type) {
        params.set('type', newFilters.type);
      } else {
        params.delete('type');
      }
    }

    if (newFilters.search !== undefined) {
      if (newFilters.search) {
        params.set('search', newFilters.search);
      } else {
        params.delete('search');
      }
    }

    if (newFilters.status !== undefined) {
      params.delete('status');
      newFilters.status?.forEach(s => params.append('status', s));
    }

    if (newFilters.priority !== undefined) {
      params.delete('priority');
      newFilters.priority?.forEach(p => params.append('priority', p));
    }

    params.set('page', '1'); // Reset to first page on filter change
    setSearchParams(params);
  };

  const updateSort = (field: string) => {
    const params = new URLSearchParams(searchParams);
    if (sort.field === field) {
      params.set('sort_direction', sort.direction === 'asc' ? 'desc' : 'asc');
    } else {
      params.set('sort_by', field);
      params.set('sort_direction', 'desc');
    }
    setSearchParams(params);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Your Items</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage your feature requests, feedback, and bug reports
          </p>
        </div>
        <Link
          to="/items/new"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          New Item
        </Link>
      </div>

      {/* Search and filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={filters.search || ''}
              onChange={(e) => updateFilters({ search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Type filter */}
          <select
            value={filters.type || ''}
            onChange={(e) => updateFilters({ type: e.target.value as ItemType || undefined })}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Types</option>
            {Object.entries(typeConfig).map(([type, config]) => (
              <option key={type} value={type}>{config.label}</option>
            ))}
          </select>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={clsx(
              'inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium transition-colors',
              showFilters
                ? 'border-primary-500 text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            )}
          >
            <Filter className="mr-2 h-4 w-4" />
            Filters
          </button>
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Status filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(statusConfig).map(([status, config]) => (
                  <button
                    key={status}
                    onClick={() => {
                      const currentStatuses = filters.status || [];
                      const newStatuses = currentStatuses.includes(status as ItemStatus)
                        ? currentStatuses.filter(s => s !== status)
                        : [...currentStatuses, status as ItemStatus];
                      updateFilters({ status: newStatuses });
                    }}
                    className={clsx(
                      'px-3 py-1 rounded-full text-xs font-medium transition-colors',
                      filters.status?.includes(status as ItemStatus)
                        ? `${config.bgColor} ${config.color}`
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    )}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Priority filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Priority
              </label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(priorityConfig).map(([priority, config]) => (
                  <button
                    key={priority}
                    onClick={() => {
                      const currentPriorities = filters.priority || [];
                      const newPriorities = currentPriorities.includes(priority as ItemPriority)
                        ? currentPriorities.filter(p => p !== priority)
                        : [...currentPriorities, priority as ItemPriority];
                      updateFilters({ priority: newPriorities });
                    }}
                    className={clsx(
                      'px-3 py-1 rounded-full text-xs font-medium transition-colors',
                      filters.priority?.includes(priority as ItemPriority)
                        ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    )}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Items list */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Table header */}
        <div className="hidden sm:grid sm:grid-cols-12 gap-4 px-6 py-3 border-b border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-500 dark:text-gray-400">
          <div className="col-span-4 flex items-center">
            <button onClick={() => updateSort('title')} className="flex items-center hover:text-gray-700 dark:hover:text-gray-200">
              Title
              {sort.field === 'title' && (sort.direction === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />)}
            </button>
          </div>
          <div className="col-span-1 flex items-center">
            <button onClick={() => updateSort('type')} className="flex items-center hover:text-gray-700 dark:hover:text-gray-200">
              Type
              {sort.field === 'type' && (sort.direction === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />)}
            </button>
          </div>
          <div className="col-span-2 flex items-center">
            <button onClick={() => updateSort('status')} className="flex items-center hover:text-gray-700 dark:hover:text-gray-200">
              Status
              {sort.field === 'status' && (sort.direction === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />)}
            </button>
          </div>
          <div className="col-span-1 flex items-center">
            <button onClick={() => updateSort('priority')} className="flex items-center hover:text-gray-700 dark:hover:text-gray-200">
              Priority
              {sort.field === 'priority' && (sort.direction === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />)}
            </button>
          </div>
          <div className="col-span-2 flex items-center">
            <button onClick={() => updateSort('created_at')} className="flex items-center hover:text-gray-700 dark:hover:text-gray-200">
              Date
              {sort.field === 'created_at' && (sort.direction === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />)}
            </button>
          </div>
          <div className="col-span-2 flex items-center">
            <button onClick={() => updateSort('vote_count')} className="flex items-center hover:text-gray-700 dark:hover:text-gray-200">
              Votes
              {sort.field === 'vote_count' && (sort.direction === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />)}
            </button>
          </div>
        </div>

        {/* Items */}
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            Loading...
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            No items found. Try adjusting your filters.
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {data?.items.map((item) => {
              const typeConf = typeConfig[item.type];
              const statusConf = statusConfig[item.status];
              const priorityConf = priorityConfig[item.priority];
              const TypeIcon = typeConf.icon;

              return (
                <Link
                  key={item.id}
                  to={`/items/${item.id}`}
                  className="block hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="px-6 py-4 sm:grid sm:grid-cols-12 sm:gap-4 sm:items-center">
                    {/* Title */}
                    <div className="col-span-4">
                      <div className="flex items-start space-x-3">
                        <TypeIcon className={clsx('h-5 w-5 mt-0.5 flex-shrink-0', typeConf.color)} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {item.title}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 sm:hidden">
                            by {item.created_by_name}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Type - mobile */}
                    <div className="sm:hidden mt-2 flex items-center gap-2 text-xs">
                      <span className={clsx('px-2 py-0.5 rounded', typeConf.bgColor, typeConf.color)}>
                        {typeConf.label}
                      </span>
                      <span className={clsx('px-2 py-0.5 rounded', statusConf.bgColor, statusConf.color)}>
                        {statusConf.label}
                      </span>
                    </div>

                    {/* Type - desktop */}
                    <div className="hidden sm:block col-span-1">
                      <span className={clsx('px-2 py-1 rounded text-xs font-medium', typeConf.bgColor, typeConf.color)}>
                        {typeConf.label}
                      </span>
                    </div>

                    {/* Status - desktop */}
                    <div className="hidden sm:block col-span-2">
                      <span className={clsx('px-2 py-1 rounded text-xs font-medium', statusConf.bgColor, statusConf.color)}>
                        {statusConf.label}
                      </span>
                    </div>

                    {/* Priority */}
                    <div className="hidden sm:block col-span-1">
                      <span className={clsx('text-xs font-medium', priorityConf.color)}>
                        {priorityConf.label}
                      </span>
                    </div>

                    {/* Date */}
                    <div className="hidden sm:block col-span-2 text-sm text-gray-500 dark:text-gray-400">
                      {new Date(item.created_at).toLocaleDateString()}
                    </div>

                    {/* Votes & Comments */}
                    <div className="hidden sm:flex col-span-2 items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center">
                        <ThumbsUp className="h-4 w-4 mr-1" />
                        {item.vote_count}
                      </span>
                      <span className="flex items-center">
                        <MessageCircle className="h-4 w-4 mr-1" />
                        {item.comment_count}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page {data.page} of {data.total_pages} ({data.total} items)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set('page', String(page - 1));
                  setSearchParams(params);
                }}
                disabled={page <= 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Previous
              </button>
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set('page', String(page + 1));
                  setSearchParams(params);
                }}
                disabled={page >= data.total_pages}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
